import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { useNavigate } from 'react-router-dom';
import {
  createIdentityContextProvider,
  createAuthorizationHOC,
  createProtectedRoute,
  createUseIdentity,
  createUseHasRole,
  IdentityConfig,
  AuthorizationConfig,
  OktaAuthService,
} from '../shared/auth';
import { Roles } from '../enum/Roles';
import { Login } from '../providers/identityApi';
import logger from '../providers/logger';
import Unauthorized from '../pages/Unauthorized';
import { getSafeRedirectUrl } from '../enum/UrlAppMap';

// App-specific types
type AppRole = Roles;

// Okta configuration
const redirect = process.env.REACT_APP_OKTA_APP_REDIRECT_URL!;

export const oktaAuthConfig = new OktaAuth({
  issuer: window.REACT_APP_OKTA_ISSUER,
  clientId: window.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: window.location.origin + redirect,
});

// App-specific identity configuration
const identityConfig: IdentityConfig<AppRole> = {
  roleApi: async (accessToken: string) => {
    const roles = await Login(accessToken);
    return roles as AppRole[];
  },
  isAdminCheck: (roles: AppRole[]) => roles.includes(Roles.Admin),
  logger: {
    ...logger,
    AI: {
      info: logger.info,
    },
  },
};

// Authorization configuration
const authorizationConfig: AuthorizationConfig<AppRole> = {
  unauthorizedUrl: '/unauthorized',
  UnauthorizedComponent: Unauthorized,
  getSafeRedirectUrl: getSafeRedirectUrl,
};

// Create app-specific providers and components
export const { IdentityContext, IdentityContextProvider } = createIdentityContextProvider(identityConfig);

export const withAuthorization = createAuthorizationHOC(IdentityContext, authorizationConfig);

export const ProtectedRoute = createProtectedRoute({
  captureRedirectUrl: true,
});

// Create app-specific hooks
export const useIdentity = createUseIdentity(IdentityContext);
export const useHasRole = createUseHasRole(IdentityContext);

// Create auth service instance
export const authService = new OktaAuthService(
  {
    issuer: window.REACT_APP_OKTA_ISSUER,
    clientId: window.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: window.location.origin + redirect,
  },
  {
    scopes: ['openid', 'email'],
    logger: {
      ...logger,
      AI: {
        info: logger.info,
      },
    },
  }
);

// Create restore original URI function factory
export const useCreateRestoreOriginalUri = () => {
  const navigate = useNavigate();
  return authService.createRestoreOriginalUri(navigate, toRelativeUrl);
};
